import {
  AnimatePresence,
  motion,
  useIsPresent,
  useReducedMotion,
} from "framer-motion";
import { useState, type ReactNode } from "react";

import { SPRING } from "@/components/layout/sidebar/sidebar.constants";
import { useLocale } from "@/hooks/useLocale";
import { cn } from "@/utils/cn";

interface ISidebarSlotProps {
  isDesktop: boolean;
  /** The main menu. Always mounted. */
  mainMenu: ReactNode;
  /** The settings sidebar on main Settings, `null` everywhere else. */
  settingsMenu: ReactNode;
}

/**
 * Both sidebars occupy the same grid cell at `lg`, the settings one on top.
 * Each layer is its own stacking context, so DOM order alone decides which is
 * in front — the menus' rows raise their icons above the active highlight
 * with a z-index, and without isolation those would paint over the other
 * layer mid-slide.
 */
const STACKED = "lg:col-start-1 lg:row-start-1 lg:isolate";

/**
 * The app `Layout`'s sidebar slot: the main menu, with the settings sidebar
 * sliding over it on main Settings.
 *
 * On desktop, opening Settings slides the settings sidebar in from the
 * inline-start edge over the main menu, and leaving slides it back out to
 * reveal the main menu underneath — the same motion as the overlay below
 * `lg`. The main menu itself never moves or remounts: it stays mounted the
 * whole time (a remount would replay its own opening animation and drop its
 * accordion and search state), shows beneath the settings sidebar while that
 * slides, and leaves the layout only once it is fully covered.
 *
 * Below `lg` both sidebars are fixed overlays, so there is nothing to slide
 * over: the main menu is simply hidden on Settings and the swap is instant.
 */
export const SidebarSlot = ({
  isDesktop,
  mainMenu,
  settingsMenu,
}: ISidebarSlotProps) => {
  const onSettings = settingsMenu != null;
  // Whether the settings sidebar has finished sliding in. Starts true when
  // the app loads straight into Settings: nothing slides then.
  const [isCovered, setIsCovered] = useState(onSettings);
  const hideMainMenu = onSettings && (isCovered || !isDesktop);

  return (
    <div className="contents lg:grid lg:shrink-0 lg:grid-cols-1 lg:grid-rows-1">
      <div
        // Under the settings sidebar it can still be seen, never used.
        inert={onSettings}
        className={cn(hideMainMenu ? "hidden" : "contents lg:block", STACKED)}
      >
        {mainMenu}
      </div>

      <AnimatePresence initial={false}>
        {onSettings && (
          <SettingsLayer
            key="settings"
            animated={isDesktop}
            onCoveredChange={setIsCovered}
          >
            {settingsMenu}
          </SettingsLayer>
        )}
      </AnimatePresence>
    </div>
  );
};

interface ISettingsLayerProps {
  animated: boolean;
  onCoveredChange: (covered: boolean) => void;
  children: ReactNode;
}

const SettingsLayer = ({
  animated,
  onCoveredChange,
  children,
}: ISettingsLayerProps) => {
  const isPresent = useIsPresent();
  const reduceMotion = useReducedMotion();
  const { byDir } = useLocale();
  const offscreen = { x: byDir("-100%", "100%") };

  return (
    <motion.div
      initial={offscreen}
      animate={{ x: 0 }}
      exit={offscreen}
      transition={animated && !reduceMotion ? SPRING : { duration: 0 }}
      // The main menu shows underneath while this slides either way, and
      // drops out of the layout once this has fully slid in.
      onAnimationStart={() => onCoveredChange(false)}
      onAnimationComplete={() => onCoveredChange(isPresent)}
      // Sliding out, it is already gone as far as input is concerned.
      inert={!isPresent}
      className={cn("contents lg:block", STACKED)}
    >
      {children}
    </motion.div>
  );
};
